import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useResources } from "@/api/hooks/useBookings";
import { useCreatePractitionerBooking } from "@/api/hooks/usePractitioners";

interface BookPractitionerSessionDialogProps {
  open: boolean;
  onClose: () => void;
  practitionerId: string;
}

type FreshBookingStatus = "inquiry" | "confirmed";

// Nikki's 8/03 reprice (scripts/seed_resources.py): 90-min Aerie blocks,
// 7:00 AM-8:30 PM -> 9 blocks fit in a day.
const MIN_BLOCKS_BOOKED = 1;
const MAX_BLOCKS_BOOKED = 9;

/** Books a practitioner into The Aerie (the sole wellness_room resource
 * today, but looked up rather than hardcoded). A fresh booking can only
 * start life as an inquiry or confirmed — cancelled/completed are reached
 * via later status-transition actions on the detail page, not at create time.
 *
 * `blocksBooked`/`autopay` feed Nikki's 8/03 discount ladder
 * (app/services/practitioner_discount.py): 2+ consecutive blocks earns
 * 10% off, and autopay bumps an already-earned weekly-recurring 20%
 * discount to 25% — see that module's docstring for the full ladder. */
export function BookPractitionerSessionDialog({ open, onClose, practitionerId }: BookPractitionerSessionDialogProps) {
  const { data: resources } = useResources();
  const wellnessResources = resources?.filter((r) => r.kind === "wellness_room") ?? [];

  const [resourceId, setResourceId] = useState("");
  const [bookingDate, setBookingDate] = useState("");
  const [status, setStatus] = useState<FreshBookingStatus>("confirmed");
  const [blocksBooked, setBlocksBooked] = useState(MIN_BLOCKS_BOOKED);
  const [autopay, setAutopay] = useState(false);
  const [notes, setNotes] = useState("");

  const createBooking = useCreatePractitionerBooking();

  useEffect(() => {
    if (open) {
      const firstWellnessResourceId = resources?.find((r) => r.kind === "wellness_room")?.id ?? "";
      setResourceId(firstWellnessResourceId);
      setBookingDate("");
      setStatus("confirmed");
      setBlocksBooked(MIN_BLOCKS_BOOKED);
      setAutopay(false);
      setNotes("");
    }
  }, [open, resources]);

  const canSubmit =
    !!resourceId &&
    !!bookingDate &&
    blocksBooked >= MIN_BLOCKS_BOOKED &&
    blocksBooked <= MAX_BLOCKS_BOOKED &&
    !createBooking.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await createBooking.mutateAsync({
        practitionerId,
        resource_id: resourceId,
        booking_date: bookingDate,
        status,
        blocks_booked: blocksBooked,
        autopay,
        notes: notes.trim() || null,
      });
      toastSuccess("Session booked");
      onClose();
    } catch (err) {
      toastError("Couldn't book session", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Book a session">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>Book a session</DialogHeader>
        <DialogBody className="space-y-4">
          <FormSelect label="Room" value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
            <option value="">Select a room…</option>
            {wellnessResources.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </FormSelect>

          <FormField
            label="Date"
            type="date"
            value={bookingDate}
            onChange={(e) => setBookingDate(e.target.value)}
            data-testid="book-session-date-input"
          />

          <FormSelect label="Status" value={status} onChange={(e) => setStatus(e.target.value as FreshBookingStatus)}>
            <option value="confirmed">Confirmed</option>
            <option value="inquiry">Inquiry</option>
          </FormSelect>

          <FormField
            label="Consecutive 90-min blocks"
            type="number"
            min={MIN_BLOCKS_BOOKED}
            max={MAX_BLOCKS_BOOKED}
            value={blocksBooked}
            onChange={(e) => setBlocksBooked(Number(e.target.value))}
            hint="2 or more earns a 10% same-day discount."
            data-testid="book-session-blocks-input"
          />

          <Checkbox
            label="Automatic monthly payment (autopay)"
            checked={autopay}
            onChange={(e) => setAutopay(e.target.checked)}
            data-testid="book-session-autopay-checkbox"
          />

          <FormTextarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createBooking.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit} data-testid="submit-book-session-form">
            {createBooking.isPending ? "Booking…" : "Book session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
